import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export interface ItemCarrinho {
  chave: string; // produtoId+tamanho+cor+estampaId
  produtoId: string;
  produtoNome: string;
  produtoImagem?: string | null;
  tamanho: string;
  cor: string;
  corHex?: string;
  estampaId?: string | null;
  estampaNome?: string | null;
  estampaImagem?: string | null;
  quantidade: number;
  valorUnitario: number; // já inclui adicionais de estampa e tamanho
}

interface CarrinhoContextType {
  itens: ItemCarrinho[];
  adicionar: (item: ItemCarrinho) => void;
  remover: (chave: string) => void;
  atualizarQuantidade: (chave: string, quantidade: number) => void;
  limpar: () => void;
  total: number;
  quantidadeTotal: number;
  aberto: boolean;
  setAberto: (v: boolean) => void;
}

const CarrinhoContext = createContext<CarrinhoContextType | null>(null);
const STORAGE_KEY = 'monchery_loja_carrinho';

export function CarrinhoProvider({ children }: { children: ReactNode }) {
  const [itens, setItens] = useState<ItemCarrinho[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  });
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itens));
  }, [itens]);

  const adicionar = (item: ItemCarrinho) => {
    setItens(atual => {
      const existente = atual.find(i => i.chave === item.chave);
      if (existente) {
        return atual.map(i =>
          i.chave === item.chave ? { ...i, quantidade: i.quantidade + item.quantidade } : i
        );
      }
      return [...atual, item];
    });
    setAberto(true);
  };

  const remover = (chave: string) => setItens(atual => atual.filter(i => i.chave !== chave));

  const atualizarQuantidade = (chave: string, quantidade: number) => {
    if (quantidade < 1) return;
    setItens(atual => atual.map(i => (i.chave === chave ? { ...i, quantidade } : i)));
  };

  const limpar = () => setItens([]);

  const total = itens.reduce((s, i) => s + i.valorUnitario * i.quantidade, 0);
  const quantidadeTotal = itens.reduce((s, i) => s + i.quantidade, 0);

  return (
    <CarrinhoContext.Provider
      value={{ itens, adicionar, remover, atualizarQuantidade, limpar, total, quantidadeTotal, aberto, setAberto }}
    >
      {children}
    </CarrinhoContext.Provider>
  );
}

export function useCarrinho() {
  const ctx = useContext(CarrinhoContext);
  if (!ctx) throw new Error('useCarrinho deve ser usado dentro de CarrinhoProvider');
  return ctx;
}
